import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const subscriptionApi = {
  getSubscriptions: async (filters: any = {}) => {
    const params: Record<string, string | number> = { page: filters.page ?? 1, limit: filters.limit ?? 20 };
    for (const key of ["status", "portionSize", "planId", "programLength", "paymentStatus", "expiry", "search", "sortField", "sortOrder"]) {
      if (filters[key]) params[key] = filters[key];
    }
    for (const key of ["startDate", "endDate"]) {
      if (filters[key] && Number.isFinite(filters[key].getTime())) {
        const date = filters[key] as Date;
        // Date-picker values are calendar dates; query the full day in kitchen time.
        const midnight = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - 19800000;
        params[key] = new Date(midnight + (key === "endDate" ? 86400000 - 1 : 0)).toISOString();
      }
    }
    const response = await axios.get(`${API_URL}/api/admin/subscriptions`, {
      params,
      signal: filters.signal,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
      },
    });

    return response.data;
  },

  getSubscriptionDetails: async (subscriptionId: string, signal?: AbortSignal) => {
    const response = await axios.get(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}`,
      {
        signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );

    return response.data;
  },

  updateSubscriptionStatus: async (subscriptionId: string, status: string) => {
    const response = await axios.put(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/status`,
      { status },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );

    return response.data;
  },

  updateSubscription: async (subscriptionId: string, data: any) => {
    const response = await axios.put(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );

    return response.data;
  },

  extendSubscription: async (subscriptionId: string, days: number) => {
    const response = await axios.put(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/extend`,
      { days },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );
    return response.data;
  },

  increaseSkips: async (subscriptionId: string, count: number) => {
    const response = await axios.put(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/increase-skips`,
      { count },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );
    return response.data;
  },

  pauseSubscription: async (subscriptionId: string, startDate: Date, endDate: Date) => {
    const response = await axios.put(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/pause`,
      { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );
    return response.data;
  },

  resumeSubscription: async (subscriptionId: string) => {
    const response = await axios.put(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/resume`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );
    return response.data;
  },

  extendPauseSubscription: async (subscriptionId: string, newEndDate: Date) => {
    const response = await axios.put(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/pause/extend`,
      { newEndDate: newEndDate.toISOString() },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );
    return response.data;
  },

  verifyPayment: async (subscriptionId: string, paymentId?: string) => {
    const response = await axios.post(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/verify-payment`,
      { paymentId },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );
    return response.data;
  },

  manualActivate: async (
    subscriptionId: string,
    data: { paymentId?: string; reason: string; referenceId?: string }
  ) => {
    const response = await axios.post(
      `${API_URL}/api/admin/subscriptions/${subscriptionId}/manual-activate`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      }
    );
    return response.data;
  },
};

export default subscriptionApi;
